import { FC, useCallback, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Row, Space, Switch, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NAMESPACE, DELETE_ACTION_COLOR } from '@/constants';
import {
  useGetAdminDspFeesQuery,
  useCreateAdminDspFeeMutation,
  useUpdateAdminDspFeeMutation,
  useDeleteAdminDspFeeMutation,
  DspFee
} from '@/api';
import { formatPercentInput, parseNumericInput, type NumericInputValue } from '@/utils/inputNumber';

const { confirm } = Modal;

const TraderControlsDspFees: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const [form] = Form.useForm<{ dspName: string; feePercentage: number; isActive?: boolean }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDspFee, setEditingDspFee] = useState<DspFee | null>(null);

  const { data, isLoading } = useGetAdminDspFeesQuery({ includeInactive: true });
  const [createDspFee, { isLoading: isCreating }] = useCreateAdminDspFeeMutation();
  const [updateDspFee, { isLoading: isUpdating }] = useUpdateAdminDspFeeMutation();
  const [deleteDspFee, { isLoading: isDeleting }] = useDeleteAdminDspFeeMutation();

  const openAddModal = useCallback(() => {
    setEditingDspFee(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (record: DspFee) => {
      setEditingDspFee(record);
      form.setFieldsValue({
        dspName: record.dspName,
        feePercentage: record.feePercentage,
        isActive: record.isActive
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingDspFee(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (editingDspFee) {
        await updateDspFee({
          id: editingDspFee._id,
          body: {
            dspName: values.dspName,
            feePercentage: values.feePercentage,
            ...(values.isActive !== undefined && { isActive: values.isActive })
          }
        }).unwrap();
        message.success(t('dspFeeHasBeenEdited'));
      } else {
        await createDspFee({
          dspName: values.dspName,
          feePercentage: values.feePercentage
        }).unwrap();
        message.success(t('dspFeeHasBeenCreated'));
      }
      closeModal();
    } catch (err) {
      if (err && typeof err === 'object' && 'data' in err) {
        message.error((err as { data?: { message?: string } }).data?.message ?? 'Something went wrong');
      }
    }
  }, [editingDspFee, form, createDspFee, updateDspFee, closeModal, t]);

  const handleDelete = useCallback(
    (record: DspFee) => {
      const id = record._id;
      confirm({
        title: <span style={{ color: DELETE_ACTION_COLOR }}>{t('deleteDspFeeModalTitle')}</span>,
        content: t('deleteDspFeeModalDescription'),
        icon: <ExclamationCircleOutlined />,
        okText: t('cancel'),
        cancelText: t('delete'),
        cancelButtonProps: { danger: true, style: { color: DELETE_ACTION_COLOR } },
        onCancel: () =>
          deleteDspFee(id)
            .unwrap()
            .then(() => message.success(t('dspFeeHasBeenDeleted')))
      });
    },
    [deleteDspFee, t]
  );

  const columns: ColumnsType<DspFee> = [
    {
      title: t('dspName'),
      dataIndex: 'dspName',
      key: 'dspName',
      sorter: (a, b) => (a.dspName ?? '').localeCompare(b.dspName ?? ''),
      render: (val) => val ?? '—'
    },
    {
      title: t('feePercentage'),
      dataIndex: 'feePercentage',
      key: 'feePercentage',
      sorter: (a, b) => (a.feePercentage ?? 0) - (b.feePercentage ?? 0),
      render: (val: number) => (val != null ? `${val}%` : '—')
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val: boolean) => (val ? t('active') : t('inactive'))
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            {t('edit')}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            style={{ color: DELETE_ACTION_COLOR }}
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          >
            {t('delete')}
          </Button>
        </Space>
      )
    }
  ];

  const dspFees = data?.dspFees ?? [];

  return (
    <>
      <Typography.Text strong style={{ display: 'block', marginBottom: 16, marginTop: 16, fontSize: 14 }}>
        {t('traderControlsDspFees')}
      </Typography.Text>
      <Row style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={openAddModal}>
            {t('addDspFee')}
          </Button>
        </Row>
        <Table<DspFee>
          columns={columns}
          dataSource={dspFees.map((d) => ({ ...d, key: d._id }))}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: t('noData') }}
        />
        <Modal
          title={editingDspFee ? t('editDspFee') : t('addDspFee')}
          open={modalOpen}
          onOk={handleSubmit}
          onCancel={closeModal}
          confirmLoading={isCreating || isUpdating}
          destroyOnHidden
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="dspName"
              label={t('dspName')}
              rules={[{ required: true, message: t('requiredFieldMessage') }]}
            >
              <Input placeholder={t('dspName')} />
            </Form.Item>
            <Form.Item
              name="feePercentage"
              label={t('feePercentage')}
              rules={[{ required: true, message: t('requiredFieldMessage') }]}
            >
              <InputNumber<NumericInputValue>
                min={0}
                max={100}
                style={{ width: '100%' }}
                placeholder={t('feePercentage')}
                formatter={formatPercentInput}
                parser={parseNumericInput}
              />
            </Form.Item>
            {editingDspFee && (
              <Form.Item name="isActive" label={t('status')} valuePropName="checked">
                <Switch checkedChildren={t('active')} unCheckedChildren={t('inactive')} />
              </Form.Item>
            )}
          </Form>
        </Modal>
    </>
  );
};

export default TraderControlsDspFees;
